import React, { useState, useEffect } from 'react';
import styles from './ShopsPage.module.css';
import ApiService from '../../services/api';
import { useAuthContext } from '../../context/AuthContext';

const ShopsPage = () => {
  // ✅ Get both token AND user info from context
  const { getToken, user } = useAuthContext();

  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [newShop, setNewShop] = useState({
    name: '',
    category: '',
    location: '',
    description: '',
    contact_phone: '',
    offers_delivery: false,
    fast_service: false,
    promo_text: '',
    business_type: 'shop'
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    loadShops();
  }, []);

  const loadShops = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.fetchShops();
      setShops(data);
    } catch (err) {
      console.error('Error loading shops:', err);
      setError('Failed to load shops. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const categoryOptions = [
    { value: 'electronics', label: 'Electronics Store' },
    { value: 'fashion', label: 'Clothing & Accessories' },
    { value: 'phone_repair', label: 'Phone Repair & Services' },
    { value: 'grocery', label: 'Grocery & Fresh Produce' },
    { value: 'furniture', label: 'Furniture & Home Goods' },
    { value: 'auto_parts', label: 'Auto Parts & Accessories' },
    { value: 'restaurant', label: 'Restaurant & Food' },
    { value: 'beauty', label: 'Beauty & Personal Care' },
    { value: 'books', label: 'Books & Stationery' },
    { value: 'sports', label: 'Sports & Fitness' },
    { value: 'pharmacy', label: 'Pharmacy & Health' },
    { value: 'pets', label: 'Pet Supplies' },
    { value: 'hardware', label: 'Hardware & Tools' },
    { value: 'photography', label: 'Photography Services' },
    { value: 'cleaning', label: 'Cleaning Services' },
    { value: 'plumbing', label: 'Plumbing Services' },
    { value: 'electrical', label: 'Electrical Services' },
    { value: 'carpentry', label: 'Carpentry Services' },
    { value: 'tailoring', label: 'Tailoring & Alterations' },
    { value: 'catering', label: 'Catering Services' },
    { value: 'transportation', label: 'Transportation Services' },
    { value: 'laundry', label: 'Laundry Services' },
    { value: 'salon', label: 'Salon & Spa Services' },
    { value: 'tutoring', label: 'Tutoring & Education' },
    { value: 'event_planning', label: 'Event Planning' },
    { value: 'mechanic', label: 'Auto Repair & Mechanic' },
    { value: 'landscaping', label: 'Landscaping & Gardening' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewShop(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Validate required fields
    if (!newShop.name || !newShop.category || !newShop.location) {
      alert('Please fill in all required fields');
      return;
    }

    // ✅ Check authentication
    const token = getToken();
    if (!token) {
      alert('Please login to create a shop');
      return;
    }

    // ✅ DEBUG: Log user info
    console.log('==========================================');
    console.log('🏪 CREATING SHOP');
    console.log('==========================================');
    console.log('👤 Logged in user:', user);
    console.log('📧 User email:', user?.identifier || user?.email);
    console.log('🔑 Token:', token ? 'Present' : 'Missing');
    console.log('📝 Shop data:', newShop);
    console.log('==========================================');

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('name', newShop.name);
      formData.append('category', newShop.category);
      formData.append('location', newShop.location);
      formData.append('description', newShop.description || `Welcome to ${newShop.name}`);
      formData.append('contact_phone', newShop.contact_phone || '');
      formData.append('offers_delivery', newShop.offers_delivery);
      formData.append('fast_service', newShop.fast_service);
      formData.append('promo_text', newShop.promo_text || 'New shop • Check us out!');
      formData.append('business_type', newShop.business_type);
      formData.append('is_active', 'true');

      if (imageFile) {
        formData.append('cover_image', imageFile);
      }

      // ✅ DEBUG: Log FormData contents
      console.log('\n📦 FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}:`, value);
      }

      const result = await ApiService.postShop(formData, token);

      console.log('\n✅ Shop creation result:', result);

      if (!result.success) {
        alert(result.error || 'Failed to create shop');
        return;
      }

      // Reload shops
      await loadShops();

      // Reset form
      setNewShop({
        name: '',
        category: '',
        location: '',
        description: '',
        contact_phone: '',
        offers_delivery: false,
        fast_service: false,
        promo_text: '',
        business_type: 'shop'
      });
      setImageFile(null);
      setImagePreview(null);
      setShowModal(false);

      alert('Shop created successfully!');
    } catch (err) {
      console.error('❌ Error creating shop:', err);
      console.error('Error details:', err.response?.data);
      alert(err.response?.data?.detail || 'Failed to create shop. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setNewShop({
      name: '',
      category: '',
      location: '',
      description: '',
      contact_phone: '',
      offers_delivery: false,
      fast_service: false,
      promo_text: '',
      business_type: 'shop'
    });
    setImageFile(null);
    setImagePreview(null);
  };

  const getCategoryLabel = (value) => {
    const category = categoryOptions.find(cat => cat.value === value);
    return category ? category.label : value;
  };

  return (
    <div className={styles.shopsPageContainer}>
      <div className={styles.shopsContentWrapper}>
        <div className={styles.shopsHeaderSection}>
          <h1 className={styles.shopsMainTitle}>Featured Shops & Services</h1>
          <button className={styles.addShopBtn} onClick={() => setShowModal(true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="16"/>
              <line x1="8" y1="12" x2="16" y2="12"/>
            </svg>
            Add Your Shop
          </button>
        </div>

        <div className={styles.shopsGrid}>
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner}></div>
              <p>Loading shops...</p>
            </div>
          ) : error ? (
            <div className={styles.errorState}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <h3>{error}</h3>
              <button onClick={loadShops} className={styles.retryBtn}>Try Again</button>
            </div>
          ) : shops.length === 0 ? (
            <div className={styles.emptyState}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              <h3>No shops yet</h3>
              <p>Click "Add Your Shop" to get started and showcase your business</p>
            </div>
          ) : (
            shops.map((shop) => (
              <div key={shop.id} className={styles.shopCard}>
                <div className={styles.shopHeaderImage}>
                  <img
                    src={shop.cover_image_url || 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=400&fit=crop'}
                    alt={shop.name}
                    className={styles.shopCoverImage}
                    loading="lazy"
                  />
                </div>
                <div className={styles.shopContent}>
                  <div className={styles.shopTitleRow}>
                    <h3 className={styles.shopName}>{shop.name}</h3>
                    <button className={styles.shopEditBtn}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                  </div>
                  <p className={styles.shopCategory}>{shop.category_display || getCategoryLabel(shop.category)}</p>
                  <div className={styles.shopLocation}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    <span>{shop.location}</span>
                  </div>
                  {shop.rating > 0 && (
                    <div className={styles.shopRating}>
                      <span className={styles.ratingStars}>★★★★★</span>
                      <span className={styles.ratingValue}>{shop.rating}</span>
                    </div>
                  )}
                  <div className={styles.shopFeatures}>
                    {shop.offers_delivery && (
                      <span className={styles.shopFeature}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="1" y="3" width="15" height="13"/>
                          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                          <circle cx="5.5" cy="18.5" r="2.5"/>
                          <circle cx="18.5" cy="18.5" r="2.5"/>
                        </svg>
                        Offers delivery
                      </span>
                    )}
                    {shop.fast_service && (
                      <span className={styles.shopFeature}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        Fast service
                      </span>
                    )}
                  </div>
                  {shop.promo_text && (
                    <p className={styles.shopPromo}>{shop.promo_text}</p>
                  )}
                  <div className={styles.shopOrders}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    <span>{shop.total_orders} Orders</span>
                  </div>
                  <button className={styles.shopManageBtn}>
                    Manage orders, promote & chat with customers
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Shop Modal */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Add Your Shop</h2>
              <button className={styles.closeBtn} onClick={handleCloseModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.shopForm}>
              <div className={styles.formGroup}>
                <label htmlFor="name">Shop Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={newShop.name}
                  onChange={handleInputChange}
                  placeholder="Enter your shop name"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  name="category"
                  value={newShop.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a category</option>
                  {categoryOptions.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="location">Location *</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={newShop.location}
                  onChange={handleInputChange}
                  placeholder="e.g., Nairobi, KE"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="image">Shop Image</label>
                <div className={styles.imageUploadContainer}>
                  <input
                    type="file"
                    id="image"
                    name="image"
                    accept="image/*"
                    onChange={handleImageChange}
                    className={styles.fileInput}
                  />
                  <label htmlFor="image" className={styles.fileInputLabel}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                    {imageFile ? imageFile.name : 'Choose an image'}
                  </label>
                  {imagePreview && (
                    <div className={styles.imagePreview}>
                      <img src={imagePreview} alt="Preview" />
                      <button
                        type="button"
                        onClick={() => { setImageFile(null); setImagePreview(null); }}
                        className={styles.removeImageBtn}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"/>
                          <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="contact_phone">Contact Phone</label>
                <input
                  type="tel"
                  id="contact_phone"
                  name="contact_phone"
                  value={newShop.contact_phone}
                  onChange={handleInputChange}
                  placeholder="e.g., +254712345678"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="description">Description</label>
                <input
                  type="text"
                  id="description"
                  name="description"
                  value={newShop.description}
                  onChange={handleInputChange}
                  placeholder={`Welcome to ${newShop.name || 'your shop'}`}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="promo_text">Promotional Message</label>
                <input
                  type="text"
                  id="promo_text"
                  name="promo_text"
                  value={newShop.promo_text}
                  onChange={handleInputChange}
                  placeholder="e.g., Grand opening • 20% off"
                />
              </div>

              <div className={styles.checkboxGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="offers_delivery"
                    checked={newShop.offers_delivery}
                    onChange={handleInputChange}
                  />
                  <span>Offers Delivery</span>
                </label>

                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="fast_service"
                    checked={newShop.fast_service}
                    onChange={handleInputChange}
                  />
                  <span>Fast Service</span>
                </label>
              </div>

              <div className={styles.formActions}>
                <button type="button" className={styles.cancelBtn} onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className={styles.submitBtn} disabled={loading}>
                  {loading ? 'Creating...' : 'Add Shop'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopsPage;